import * as React from 'react'

import {
  CheckIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  Trash2Icon,
} from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import Keys from '~/client/src/shared/enums/Keys'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import BaseStruxhubInput, { ISharedProps } from '../BaseStruxhubInput'
import StruxhubInput from '../StruxhubInput'
import CaretDownSelectorIcon from '../StruxhubSelector/CaretDownSelectorIcon'

import './StruxhubSearchableSelect.scss'

export interface SelectOption {
  value: string | number | null
  label: string
  deleted?: boolean
  isNew?: boolean
}

interface IProps extends Omit<ISharedProps, 'value'> {
  options: SelectOption[]
  value?: string | number | null | (string | number | null)[]
  onChange?: (
    value: string | number | null | (string | number | null)[],
  ) => void
  placeholder?: string
  multiSelect?: boolean
  allowUserCreation?: boolean
  onOptionsChanged?: (options: SelectOption[]) => void
}

const StruxhubSearchableSelect: React.FC<IProps> = observer(
  ({
    options,
    value,
    onChange,
    placeholder,
    multiSelect = false,
    allowUserCreation = true,
    onOptionsChanged,
    isDisabled,
    isReadOnly,
    ...baseProps
  }) => {
    const [isOpen, setIsOpen] = React.useState(false)
    const [searchQuery, setSearchQuery] = React.useState('')
    const [highlightedIndex, setHighlightedIndex] = React.useState(0)
    const [isCreatingNew, setIsCreatingNew] = React.useState(false)
    const [editingOptionValue, setEditingOptionValue] = React.useState<
      string | number | null | undefined
    >(undefined)
    const [editValue, setEditValue] = React.useState('')

    const transformLabelToValue = (label: string): string => {
      return label
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
    }

    const updateOptions = (newOptions: SelectOption[]) => {
      onOptionsChanged(newOptions)
    }

    const selectedValues = React.useMemo((): (string | number | null)[] => {
      if (multiSelect) {
        return Array.isArray(value) ? value : []
      }
      if (Array.isArray(value)) {
        return value.length > 0 ? [value[0]] : []
      }
      return value !== undefined && value !== '' ? [value] : []
    }, [value, multiSelect])

    const filteredOptions = React.useMemo(() => {
      const query = searchQuery.toLowerCase()
      return options.filter(option => {
        const matchesSearch = option.label.toLowerCase().includes(query)
        const isSelected = selectedValues.includes(option.value)
        const shouldShow = !option.deleted || isSelected
        return matchesSearch && shouldShow
      })
    }, [options, searchQuery, selectedValues])

    const displayValue = React.useMemo(() => {
      if (selectedValues.length === 0) return ''
      if (multiSelect) {
        return selectedValues
          .map(val => {
            const option = options.find(opt => opt.value === val)
            return option?.label || val
          })
          .join(', ')
      }
      const option = options.find(opt => opt.value === selectedValues[0])
      return option?.label || selectedValues[0]
    }, [selectedValues, options, multiSelect])

    React.useEffect(() => {
      if (!isOpen) {
        setSearchQuery('')
        setHighlightedIndex(0)
      }
    }, [isOpen])

    const handleOptionClick = (optionValue: string | number | null) => {
      if (multiSelect) {
        const newValues = selectedValues.includes(optionValue)
          ? selectedValues.filter(v => v !== optionValue)
          : [...selectedValues, optionValue]
        onChange?.(newValues)
      } else {
        onChange?.(optionValue)
        setIsOpen(false)
      }
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (!isOpen) {
        if (event.key === Keys.Enter || event.key === ' ') {
          event.preventDefault()
          setIsOpen(true)
        }
        return
      }

      switch (event.key) {
        case Keys.Escape:
          event.preventDefault()
          setIsOpen(false)
          break
        case Keys.ArrowDown:
          event.preventDefault()
          setHighlightedIndex(prev =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev,
          )
          break
        case Keys.ArrowUp:
          event.preventDefault()
          setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0))
          break
        case Keys.Enter:
          event.preventDefault()
          if (filteredOptions[highlightedIndex]) {
            handleOptionClick(filteredOptions[highlightedIndex].value)
          }
          break
      }
    }

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value)
      setHighlightedIndex(0)
    }

    const handleAddOptionClick = () => {
      setIsCreatingNew(true)
      setEditValue('')
    }

    const handleCancelCreation = () => {
      setIsCreatingNew(false)
      setEditValue('')
    }

    const handleSaveNewOption = () => {
      const trimmedLabel = editValue.trim()
      if (!trimmedLabel) {
        return
      }

      const newValue = transformLabelToValue(trimmedLabel)

      const isDuplicate = options.some(
        opt => opt.value === newValue && !opt.deleted,
      )

      if (isDuplicate) {
        return
      }

      const newOption: SelectOption = {
        value: newValue,
        label: trimmedLabel,
        isNew: true,
      }

      const updatedOptions = [...options, newOption]
      updateOptions(updatedOptions)

      onChange?.(multiSelect ? [...selectedValues, newValue] : newValue)

      setIsCreatingNew(false)
      setEditValue('')
    }

    const handleStartEdit = (optionValue: string | number | null) => {
      const option = options.find(opt => opt.value === optionValue)
      if (option) {
        setEditingOptionValue(optionValue)
        setEditValue(option.label)
      }
    }

    const handleCancelEdit = () => {
      setEditingOptionValue(undefined)
      setEditValue('')
    }

    const handleSaveEdit = () => {
      const trimmedLabel = editValue.trim()
      if (!trimmedLabel || editingOptionValue === undefined) {
        return
      }

      const option = options.find(opt => opt.value === editingOptionValue)
      if (!option) {
        return
      }

      const newValue = option.isNew
        ? transformLabelToValue(trimmedLabel)
        : trimmedLabel

      const isDuplicate = options.some(
        opt =>
          opt.value === newValue &&
          opt.value !== editingOptionValue &&
          !opt.deleted,
      )

      if (isDuplicate) {
        return
      }

      const updatedOptions = options.map(opt => {
        if (opt.value === editingOptionValue) {
          return {
            ...opt,
            label: trimmedLabel,
            value: newValue,
          }
        }
        return opt
      })

      updateOptions(updatedOptions)

      if (selectedValues.includes(editingOptionValue)) {
        const updatedSelectedValues = selectedValues.map(v =>
          v === editingOptionValue ? newValue : v,
        )
        onChange?.(multiSelect ? updatedSelectedValues : newValue)
      }

      setEditingOptionValue(undefined)
      setEditValue('')
    }

    const handleDeleteOption = (optionValue: string | number | null) => {
      const updatedOptions = options.filter(opt => opt.value !== optionValue)
      updateOptions(updatedOptions)

      if (selectedValues.includes(optionValue)) {
        const updatedSelectedValues = selectedValues.filter(
          v => v !== optionValue,
        )
        onChange?.(multiSelect ? updatedSelectedValues : '')
      }
    }

    const popupContent = (
      <div className="searchable-select-popup" onKeyDown={handleKeyDown}>
        <div className="searchable-select-search">
          <StruxhubInput
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={Localization.translator.search}
            customRightIcon={<SearchIcon size={16} />}
            isMinimalisticMode={true}
            isHelperTextHidden={true}
            autoFocus={true}
          />
        </div>

        <div className="searchable-select-options">
          {filteredOptions.length === 0 && !isCreatingNew ? (
            <div className="searchable-select-no-results">
              {Localization.translator.noResults}
            </div>
          ) : (
            filteredOptions.map(option => {
              const isSelected = selectedValues.includes(option.value)
              const isEditing = editingOptionValue === option.value

              if (isEditing) {
                return (
                  <div
                    key={option.value === null ? '__none__' : option.value}
                    className="searchable-select-option-edit"
                  >
                    <StruxhubInput
                      value={editValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEditValue(e.target.value)
                      }
                      isLabelHidden={true}
                      isHelperTextHidden={true}
                      autoFocus
                    />
                    <div className="searchable-select-option-edit-actions">
                      <Button
                        variant="primary-outline"
                        className="mx4"
                        onClick={handleCancelEdit}
                      >
                        {Localization.translator.cancel}
                      </Button>
                      <Button
                        variant="default"
                        className="mx4"
                        onClick={handleSaveEdit}
                      >
                        {Localization.translator.save}
                      </Button>
                    </div>
                  </div>
                )
              }

              return (
                <div
                  key={option.value === null ? '__none__' : option.value}
                  className={classList({
                    'searchable-select-option': true,
                    selected: isSelected,
                    'has-actions':
                      allowUserCreation &&
                      option.isNew &&
                      option.value !== null,
                  })}
                  onClick={() => handleOptionClick(option.value)}
                >
                  <span className="searchable-select-option-label">
                    {option.label}
                  </span>
                  <div className="searchable-select-option-right">
                    {allowUserCreation &&
                      option.isNew &&
                      option.value !== null && (
                        <div className="searchable-select-option-actions">
                          <button
                            className="searchable-select-action-btn"
                            onClick={e => {
                              e.stopPropagation()
                              handleStartEdit(option.value)
                            }}
                            type="button"
                          >
                            <PencilIcon size={16} />
                          </button>
                          <button
                            className="searchable-select-action-btn"
                            onClick={e => {
                              e.stopPropagation()
                              handleDeleteOption(option.value)
                            }}
                            type="button"
                          >
                            <Trash2Icon
                              size={16}
                              className="tw:text-destructive"
                            />
                          </button>
                        </div>
                      )}
                    {isSelected && (
                      <CheckIcon className="searchable-select-checkmark" />
                    )}
                  </div>
                </div>
              )
            })
          )}

          {allowUserCreation && isCreatingNew && (
            <div className="searchable-select-option-edit">
              <StruxhubInput
                value={editValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditValue(e.target.value)
                }
                placeholder={Localization.translator.optionName}
                isLabelHidden={true}
                isHelperTextHidden={true}
                autoFocus
              />
              <div className="searchable-select-option-edit-actions">
                <Button
                  variant="primary-outline"
                  className="mx4"
                  onClick={handleCancelCreation}
                >
                  {Localization.translator.cancel}
                </Button>
                <Button
                  variant="default"
                  className="mx4"
                  onClick={handleSaveNewOption}
                >
                  {Localization.translator.save}
                </Button>
              </div>
            </div>
          )}

          {allowUserCreation && !isCreatingNew && (
            <div
              className="searchable-select-add-option"
              onClick={handleAddOptionClick}
            >
              <PlusIcon size={16} />
              <span>{Localization.translator.addOption}</span>
            </div>
          )}
        </div>
      </div>
    )

    return (
      <BaseStruxhubInput {...baseProps}>
        {(isValueInvalid, _isInFocus, onFocus, onBlur) => (
          <div className="searchable-select-container">
            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <div className="tw:flex tw:items-center bare-select-wrapper full-width">
                <PopoverTrigger
                  nativeButton={false}
                  disabled={isDisabled || isReadOnly}
                  render={<div />}
                  className={classList({
                    'searchable-select-trigger pointer row y-center h40 bg-palette-brand-lightest no-outline brada4 py7 pl8 pr24 text ellipsis extra-large full-width tw:flex-1': true,
                    'ba-palette-brand-lighter': !isValueInvalid,
                    'ba-red': isValueInvalid,
                    disabled: isDisabled,
                    readonly: isReadOnly,
                  })}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  tabIndex={isDisabled || isReadOnly ? -1 : 0}
                >
                  <span
                    className={classList({
                      'searchable-select-value ellipsis': true,
                      placeholder: !displayValue,
                    })}
                  >
                    {displayValue ||
                      placeholder ||
                      Localization.translator.select}
                  </span>
                </PopoverTrigger>
                <CaretDownSelectorIcon />
              </div>
              <PopoverContent
                align="start"
                sideOffset={5}
                className="tw:w-(--anchor-width) tw:gap-0 tw:overflow-hidden tw:p-0"
              >
                {popupContent}
              </PopoverContent>
            </Popover>
          </div>
        )}
      </BaseStruxhubInput>
    )
  },
)

export default StruxhubSearchableSelect
